import { interpolate, spring, useVideoConfig } from 'remotion';

// Types
import type { Scene5bCardData } from '../../types';

// Constants
import { CLAMP_BOTH } from '@/constants';
import {
  SCENE5B_ANIMATION,
  SCENE5B_COLORS,
  SCENE5B_LAYOUT,
  SCENE5B_REVEAL_END_FRAME,
} from '../../constants';

// Components
import { TrainingTitle } from '../TrainingTitle';
import { TrainingDate } from '../TrainingDate';
import { TrainingSupporters } from '../TrainingSupporters';

interface TrainingCardProps {
  card: Scene5bCardData;
  frame: number;
}

export const TrainingCard = ({ card, frame }: TrainingCardProps) => {
  const { fps } = useVideoConfig();
  const { background } = SCENE5B_ANIMATION;

  // Spring progress from 0 to 1 for the card entrance
  const bgProgress = spring({
    frame,
    fps,
    config: background.spring,
  });

  // Slide upward from riseDistance to 0 as spring progresses
  const bgTranslateY = interpolate(
    bgProgress,
    [0, 1],
    [background.riseDistance, 0],
    CLAMP_BOTH,
  );

  return (
    <div
      className="absolute inset-0 origin-center"
      style={{ transform: `translateX(${card.targetOffsetX}px)` }}
    >
      {/* Background circle */}
      <div
        className="absolute z-[1] rounded-full"
        style={{
          ...SCENE5B_LAYOUT.circle,
          backgroundColor: SCENE5B_COLORS.circle,
          opacity: bgProgress, // Fade in with spring
          transform: `translateY(${bgTranslateY}px)`, // Rise upward into position
        }}
      />

      {/* Polygon card */}
      <div
        className="absolute z-[2]"
        style={{
          ...SCENE5B_LAYOUT.card,
          backgroundColor: SCENE5B_COLORS.card,
          opacity: bgProgress, // Fade in with spring
          transform: `translateY(${bgTranslateY}px)`, // Rise upward into position
        }}
      />

      {/* Training title (text reveal bottom to top) */}
      <TrainingTitle
        titleText={card.titleText}
        titleFontSize={card.titleFontSize}
        frame={frame}
      />

      {/* Date label (fade in left to right) */}
      {card.dateLabel && (
        <TrainingDate dateLabel={card.dateLabel} frame={frame} />
      )}

      {/* Supporter avatars (spring stagger) */}
      {frame >= SCENE5B_REVEAL_END_FRAME && card.avatars.length > 0 && (
        <TrainingSupporters card={card} frame={frame} />
      )}
    </div>
  );
};
